import { type FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  accountQuery,
  COMPLEX_PASSWORD_PATTERN,
  editPlayerDetails,
  parseBirthDateParts,
  playerProfileQuery,
  type MachineProfile,
  useAuthSession,
} from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { Button, Input, Toast } from '@/ui/common';

export interface PlayerDetailsFormProps {
  className?: string;
}

function SectionTitle({ children }: { children: string }) {
  return <p className="text-[13px] font-bold text-text-primary">{children}</p>;
}

function Divider() {
  return (
    <div className="flex w-full items-center justify-center overflow-hidden py-1">
      <div className="h-px w-full bg-border-default" />
    </div>
  );
}

export function PlayerDetailsForm({ className }: PlayerDetailsFormProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { user } = useAuthSession();
  const queryClient = useQueryClient();
  const { data: account } = useQuery(accountQuery());
  const machineGuid = user?.Guid ?? null;
  const { data: profile, isLoading, isError } = useQuery(playerProfileQuery(machineGuid));

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'success' | 'danger'; message: string } | null>(
    null,
  );

  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const complexPassword = account?.ComplexPassword === true;

  useEffect(() => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFieldError(null);
  }, [profile?.Username, profile?.Email]);

  const mutation = useMutation({
    mutationFn: (payload: Parameters<typeof editPlayerDetails>[0]) => editPlayerDetails(payload),
    onSuccess: async (result) => {
      if (result.Success === false) {
        const message =
          typeof result.Error === 'string' && result.Error.trim()
            ? result.Error
            : label('Could not save changes', 'Could not save changes');
        setToast({ tone: 'danger', message });
        return;
      }
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setFieldError(null);
      setToast({
        tone: 'success',
        message: label('Password updated', 'Password updated'),
      });
      if (machineGuid) {
        await queryClient.invalidateQueries({ queryKey: playerProfileQuery(machineGuid).queryKey });
      }
    },
    onError: (error) => {
      const message =
        error instanceof Error && error.message
          ? error.message
          : label('Could not save changes', 'Could not save changes');
      setToast({ tone: 'danger', message });
    },
  });

  const birth = parseBirthDateParts(profile?.BirthDate);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setFieldError(null);
    setToast(null);

    const hasPasswordIntent =
      oldPassword.length > 0 || newPassword.length > 0 || confirmPassword.length > 0;
    if (!hasPasswordIntent) {
      setFieldError(
        label('Enter your current and new password to save changes', 'Enter your current and new password to save changes'),
      );
      return;
    }

    if (!oldPassword || !newPassword || !confirmPassword) {
      setFieldError(label('All password fields are required', 'All password fields are required'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setFieldError(label('PasswordsDoNotMatch', 'Passwords do not match'));
      return;
    }

    if (complexPassword && !COMPLEX_PASSWORD_PATTERN.test(newPassword)) {
      setFieldError(
        label(
          'Password must be at least 8 characters and include upper, lower, number and special character',
          'Password must be at least 8 characters and include upper, lower, number and special character',
        ),
      );
      return;
    }

    if (!machineGuid) {
      setToast({
        tone: 'danger',
        message: label('Session expired. Please sign in again.', 'Session expired. Please sign in again.'),
      });
      return;
    }

    mutation.mutate({
      MachineGuid: machineGuid,
      Username: profile?.Username ?? user?.Username ?? '',
      Email: profile?.Email ?? user?.Email ?? '',
      OldPassword: oldPassword,
      NewPassword: newPassword,
      ConfirmPassword: confirmPassword,
    });
  };

  if (isLoading) {
    return (
      <p className="text-body-m text-text-secondary">{label('Loading…', 'Loading…')}</p>
    );
  }

  if (isError || !profile) {
    return (
      <p className="text-body-m text-status-danger">
        {label('Could not load profile details', 'Could not load profile details')}
      </p>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={cn('flex w-full max-w-[560px] flex-col gap-3', className)}
      noValidate
    >
      {toast ? (
        <Toast tone={toast.tone} onDismiss={() => setToast(null)}>
          {toast.message}
        </Toast>
      ) : null}

      <SectionTitle>{label('Personal', 'Personal')}</SectionTitle>
      <ReadOnlyField
        name="username"
        value={profile.Username ?? ''}
        placeholder={label('Username', 'Username')}
      />
      <div className="flex w-full gap-2">
        <ReadOnlyField
          name="firstName"
          value={profile.FirstName ?? ''}
          placeholder={label('First Name', 'First Name')}
          className="flex-1"
        />
      </div>
      <ReadOnlyField
        name="lastName"
        value={profile.LastName ?? ''}
        placeholder={label('Last Name', 'Last Name')}
      />
      <div className="flex w-full gap-2">
        <ReadOnlyField name="dobDay" value={birth.day} placeholder="DD" className="flex-1" />
        <ReadOnlyField name="dobMonth" value={birth.month} placeholder="MM" className="flex-1" />
        <ReadOnlyField name="dobYear" value={birth.year} placeholder="YYYY" className="flex-1" />
      </div>

      <Divider />
      <SectionTitle>{label('Contact', 'Contact')}</SectionTitle>
      <ReadOnlyField
        name="email"
        value={profile.Email ?? ''}
        placeholder={label('Email Address', 'Email Address')}
      />
      <ReadOnlyField
        name="telephone"
        value={profile.Telephone ?? ''}
        placeholder={label('Phone', '+90 xxx xxx xx xx')}
      />

      <Divider />
      <SectionTitle>{label('Location', 'Location')}</SectionTitle>
      <div className="flex w-full gap-2">
        <ReadOnlyField
          name="country"
          value={profile.Country ?? ''}
          placeholder={label('Country', 'Country')}
          className="flex-1"
        />
        <ReadOnlyField
          name="currency"
          value={currencyLabel(profile)}
          placeholder={label('Currency', 'Currency')}
          className="flex-1"
        />
      </div>

      <Divider />
      <SectionTitle>{label('Security', 'Security')}</SectionTitle>
      <Input
        name="oldPassword"
        type="password"
        autoComplete="current-password"
        value={oldPassword}
        onChange={(event) => setOldPassword(event.target.value)}
        placeholder={label('Current Password', 'Current Password')}
      />
      <Input
        name="newPassword"
        type="password"
        autoComplete="new-password"
        value={newPassword}
        onChange={(event) => setNewPassword(event.target.value)}
        placeholder={label('New Password', 'New Password')}
      />
      <Input
        name="confirmPassword"
        type="password"
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(event) => setConfirmPassword(event.target.value)}
        placeholder={label('Confirm Password', 'Confirm Password')}
        error={fieldError ?? undefined}
      />

      <Button type="submit" variant="primary" size="lg" fullWidth loading={mutation.isPending}>
        {label('Save changes', 'Save changes')}
      </Button>
    </form>
  );
}

function ReadOnlyField({
  name,
  value,
  placeholder,
  className,
}: {
  name: string;
  value: string;
  placeholder: string;
  className?: string;
}) {
  return (
    <Input
      name={name}
      value={value}
      placeholder={placeholder}
      disabled
      readOnly
      className={className}
    />
  );
}

function currencyLabel(profile: MachineProfile): string {
  return profile.Currency?.trim() || '';
}
